import { joinNavItemGroups } from '@billy/design-system'
import { TFunction } from 'i18next'

import { InvoiceStatus } from '../../../enums/invoiceStatus'
import { InvoicesTableStaticActionType } from '../enums/invoicesTableStaticActionType'
import { InvoicesTableStaticActionItem } from '../types/invoicesTableStaticActionItem'

interface GetInvoicesTableStaticActionsParams {
  isCreditNote: boolean
  isDraft: boolean
  invoiceStatus: InvoiceStatus | undefined
  t: TFunction
}

export const getInvoicesTableStaticActions = ({
  isCreditNote,
  isDraft,
  invoiceStatus,
  t,
}: GetInvoicesTableStaticActionsParams): InvoicesTableStaticActionItem[] => {
  const editActionItem: InvoicesTableStaticActionItem = {
    children: t('invoices.table.static_actions.edit'),
    iconLeft: 'squarePen',
    id: InvoicesTableStaticActionType.Edit,
    value: InvoicesTableStaticActionType.Edit,
  }

  const duplicateActionItem: InvoicesTableStaticActionItem = {
    children: t('invoices.table.static_actions.duplicate'),
    iconLeft: 'copy',
    id: InvoicesTableStaticActionType.Duplicate,
    value: InvoicesTableStaticActionType.Duplicate,
  }

  const deleteDraftActionItem: InvoicesTableStaticActionItem = {
    children: t('invoices.table.static_actions.delete_draft'),
    iconLeft: 'circleX',
    id: InvoicesTableStaticActionType.DeleteDraft,
    value: InvoicesTableStaticActionType.DeleteDraft,
    variant: 'danger',
  }

  const downloadActionItem: InvoicesTableStaticActionItem = {
    children: t('invoices.table.static_actions.download_pdf'),
    iconLeft: 'download',
    id: InvoicesTableStaticActionType.DownloadPDF,
    value: InvoicesTableStaticActionType.DownloadPDF,
  }

  const sendByEmailActionItem: InvoicesTableStaticActionItem = {
    children: t('invoices.table.static_actions.send_by_email'),
    iconLeft: 'mail',
    id: InvoicesTableStaticActionType.SendByEmail,
    value: InvoicesTableStaticActionType.SendByEmail,
  }

  const sendAsEInvoiceActionItem: InvoicesTableStaticActionItem = {
    children: t('invoices.table.static_actions.send_as_e_invoice'),
    iconLeft: 'fileCode',
    id: InvoicesTableStaticActionType.SendAsEInvoice,
    value: InvoicesTableStaticActionType.SendAsEInvoice,
  }

  const cancelInvoiceActionItem: InvoicesTableStaticActionItem = {
    children: t('invoices.table.static_actions.cancel_invoice'),
    iconLeft: 'circleX',
    id: InvoicesTableStaticActionType.CancelInvoice,
    value: InvoicesTableStaticActionType.CancelInvoice,
    variant: 'danger',
  }

  const markAsSentActionItem: InvoicesTableStaticActionItem = {
    children: t('invoices.table.static_actions.mark_as_sent'),
    iconLeft: 'paperPlane',
    id: InvoicesTableStaticActionType.MarkAsSent,
    value: InvoicesTableStaticActionType.MarkAsSent,
  }

  const unmarkAsSentActionItem: InvoicesTableStaticActionItem = {
    children: t('invoices.table.static_actions.unmark_as_sent'),
    iconLeft: 'paperPlane',
    id: InvoicesTableStaticActionType.UnmarkAsSent,
    value: InvoicesTableStaticActionType.UnmarkAsSent,
  }

  const registerPaymentActionItem: InvoicesTableStaticActionItem = {
    children: t('invoices.table.static_actions.register_payment'),
    iconLeft: 'handCoins',
    id: InvoicesTableStaticActionType.RegisterPayment,
    value: InvoicesTableStaticActionType.RegisterPayment,
  }

  if (isDraft) {
    return joinNavItemGroups([[editActionItem, duplicateActionItem], [deleteDraftActionItem]])
  }

  const shareActionItems = [sendByEmailActionItem, sendAsEInvoiceActionItem, downloadActionItem]

  if (isCreditNote) {
    return joinNavItemGroups([shareActionItems, [markAsSentActionItem, unmarkAsSentActionItem]])
  }

  switch (invoiceStatus) {
    case InvoiceStatus.Unpaid:
    case InvoiceStatus.Overdue:
    case InvoiceStatus.Reminded:
    case InvoiceStatus.Open:
    case InvoiceStatus.Unsent:
    case InvoiceStatus.Sent:
      return joinNavItemGroups([
        [registerPaymentActionItem],
        shareActionItems,
        [markAsSentActionItem, unmarkAsSentActionItem, duplicateActionItem],
        [cancelInvoiceActionItem],
      ])

    case InvoiceStatus.Paid:
      return joinNavItemGroups([shareActionItems, [duplicateActionItem]])

    case InvoiceStatus.Deleted:
      return [duplicateActionItem]
  }

  return []
}
